"use client";

import { useQuery } from "@tanstack/react-query";
import type { Address } from "viem";

import { TTL } from "@/config/ttl";
import type { TokenInfo, V3Position } from "@/domain/types";
import { apiFetch } from "@/lib/apiFetch";

interface WireV3Position {
  tokenId: string;
  token0: TokenInfo;
  token1: TokenInfo;
  fee: number;
  tickLower: number;
  tickUpper: number;
  liquidity: string;
  tokensOwed0: string;
  tokensOwed1: string;
  poolAddress: Address | null;
  currentTick: number | null;
  sqrtPriceX96: string | null;
}

function revive(p: WireV3Position): V3Position {
  return {
    ...p,
    tokenId: BigInt(p.tokenId),
    liquidity: BigInt(p.liquidity),
    tokensOwed0: BigInt(p.tokensOwed0),
    tokensOwed1: BigInt(p.tokensOwed1),
    sqrtPriceX96: p.sqrtPriceX96 === null ? null : BigInt(p.sqrtPriceX96),
  };
}

async function fetchPositions(owner: Address): Promise<V3Position[]> {
  const data = await apiFetch<{ positions: WireV3Position[] }>(`/api/v3/positions/${owner}`);
  return data.positions.map(revive);
}

export function positionsQueryKey(owner: Address | undefined) {
  return ["v3-positions", owner] as const;
}

export function useV3Positions(owner: Address | undefined) {
  return useQuery({
    queryKey: positionsQueryKey(owner),
    queryFn: () => fetchPositions(owner as Address),
    enabled: Boolean(owner),
    staleTime: TTL.v3Positions,
  });
}

/** Bypasses both the server-side TTL cache and this query's own staleTime —
 * call right after a wallet write (collect/decreaseLiquidity) so the list
 * reflects the new on-chain state immediately instead of echoing back
 * pre-write data for the rest of either cache's window. */
export async function fetchFreshV3Positions(owner: Address): Promise<V3Position[]> {
  const data = await apiFetch<{ positions: WireV3Position[] }>(`/api/v3/positions/${owner}?fresh=1`);
  return data.positions.map(revive);
}
